import * as React from 'react'
import omit from 'lodash/omit'
import { usePrevious, useLocale, useTranslation } from 'hooks'
import { withTranslations } from 'helpers'
import CountrySelect from '../CountrySelect'
import SubdivisionSelect from '../SubdivisionSelect'
import Hint from '../Hint'
import InputField from '../atoms/InputField/InputField'
import type { BillingAddress } from 'types/address'

const AddressForm: React.FC<AddressFormProps> = ({
  heading,
  record = null,
  displayDelivery = false,
  defaultCountryCode,
  hint = '',
  errors = null,
  formFor = 'general',
  children = undefined,
  onAddressChange,
  onCountryCodeChange = () => void 0,
  isSellerBillingAddress = false,
  allowSellerBillingAddressCountryChange = false
}) => {
  const { locale, localeObject } = useLocale()

  const [firstName, setFirstName] = React.useState(record?.first_name || '')
  const [lastName, setLastName] = React.useState(record?.last_name || '')
  const [firstLine, setFirstLine] = React.useState(record?.line_1 || '')
  const [secondLine, setSecondLine] = React.useState(record?.line_2 || '')
  const [country, setCountry] = React.useState(
    record?.country ||
      defaultCountryCode ||
      localeObject[locale].deliveryCountry
  )
  const [state, setState] = React.useState(record?.state || '')
  const [city, setCity] = React.useState(record?.city || '')
  const [postcode, setPostcode] = React.useState(record?.postcode || '')
  const [addressErrors, setAddressErrors] = React.useState(errors)

  const { t } = useTranslation()
  const prevErrors = usePrevious(errors)

  React.useEffect(() => {
    if (onAddressChange) {
      onAddressChange({
        line_1: firstLine,
        line_2: secondLine,
        country,
        state,
        city,
        postcode
      })
    }
  }, [firstLine, secondLine, country, state, city, postcode, onAddressChange])

  React.useEffect(() => {
    if (errors !== prevErrors) {
      setAddressErrors(errors)
    }
  }, [errors, prevErrors])

  const clearInputError = (inputName: keyof BillingAddress) => {
    setAddressErrors(omit(addressErrors, inputName))
  }

  const handleCountryChange = (value: string) => {
    setCountry(value)
    setState('')
  }

  const handleSubdivisionChange = (value: string) => {
    setState(value)
    clearInputError('state')
  }

  const renderAddressFields = () => {
    let postcodeName

    switch (country) {
      case 'GB':
      case 'IE':
        postcodeName = t('react_components.address.postcode')
        break
      case 'US':
        postcodeName = t('react_components.address.zip')
        break
      default:
        postcodeName = t('react_components.address.postal_code')
    }

    return (
      <>
        <div className="field--address">
          <InputField
            labelText={t('react_components.address.address')}
            type="text"
            id="line_1"
            name="address[line_1]"
            onChange={(e) => {
              setFirstLine(e.target.value)
              clearInputError('line_1')
            }}
            value={firstLine}
            className={addressErrors?.line_1 ? 'error' : ''}
          />
          {addressErrors?.line_1 && (
            <span className="field__error-message">
              {t('react_components.address.line_one')} {addressErrors?.line_1}
            </span>
          )}
        </div>
        <div
          className={`field${addressErrors?.line_2 ? ' field--erroneous' : ''}`}
        >
          <input
            type="text"
            id="line_2"
            name="address[line_2]"
            onChange={(e) => {
              setSecondLine(e.target.value)
              clearInputError('line_2')
            }}
            value={secondLine}
          />
          {addressErrors?.line_2 && (
            <span className="field__error-message">
              {t('react_components.address.line_two')} {addressErrors?.line_2}
            </span>
          )}
        </div>
        <div className="field">
          <InputField
            labelText={t('react_components.address.city')}
            type="text"
            id="city"
            name="address[city]"
            onChange={(e) => {
              setCity(e.target.value)
              clearInputError('city')
            }}
            value={city}
            className={addressErrors?.city ? 'error' : ''}
          />
          {addressErrors?.city && (
            <span className="field__error-message">
              {t('react_components.address.city')} {addressErrors?.city}
            </span>
          )}
        </div>
        {/* Creates friction during checkout, so we're removing it for UK customers during peak */}
        {country !== 'GB' && (
          <SubdivisionSelect
            onChange={handleSubdivisionChange}
            error={addressErrors?.state || ''}
            value={state}
            country={country}
            modelName="address"
          />
        )}
        <div className="field">
          <InputField
            labelText={postcodeName}
            type="text"
            id="postcode"
            name="address[postcode]"
            onChange={(e) => {
              setPostcode(e.target.value)
              clearInputError('postcode')
            }}
            value={postcode}
            className={addressErrors?.postcode ? 'error' : ''}
          />
          {addressErrors?.postcode && (
            <span className="field__error-message">
              {postcodeName} {addressErrors?.postcode}
            </span>
          )}
        </div>
      </>
    )
  }

  return (
    <>
      {formFor === 'general' && (
        <>
          <h3 className="card__title">{heading}</h3>
          <div className="grid grid--two-up grid--gapless">
            <div className="field field--mb">
              <InputField
                id="first_name"
                labelText={t('shared.first_name')}
                type="text"
                value={firstName}
                name="address[first_name]"
                onChange={(e) => {
                  setFirstName(e.target.value)
                  clearInputError('first_name')
                }}
                className={addressErrors?.first_name ? 'error' : ''}
              />
              {addressErrors?.first_name && (
                <span className="field__error-message">
                  {t('shared.first_name')} {addressErrors?.first_name}
                </span>
              )}
            </div>
            <div className="field field--mb-lg">
              <InputField
                labelText={t('shared.last_name')}
                type="text"
                id="last_name"
                name="address[last_name]"
                onChange={(e) => {
                  setLastName(e.target.value)
                  clearInputError('last_name')
                }}
                value={lastName}
                className={addressErrors?.last_name ? 'error' : ''}
              />
              {addressErrors?.last_name && (
                <span className="field__error-message">
                  {t('shared.last_name')} {addressErrors?.last_name}
                </span>
              )}
            </div>
          </div>

          {hint && <Hint text={hint} type="info" />}
          <hr className="hr--no-mt" />
        </>
      )}
      {formFor === 'stripe-verification' && (
        <h3 className="card__title">{heading}</h3>
      )}
      <CountrySelect
        displayDelivery={displayDelivery}
        onChange={handleCountryChange}
        value={country}
        modelName="address"
        onCountryCodeChange={onCountryCodeChange}
        // If we enable VAT sellers to edit this; we may need to update the VAT rate on their active collections
        // We would then also need to show them any changes in their expected payout...
        disabled={
          isSellerBillingAddress && !allowSellerBillingAddressCountryChange
        }
      />
      {addressErrors?.country && (
        <span className="field__error-message">
          {t('react_components.address.country')} {addressErrors?.country}
        </span>
      )}
      {renderAddressFields()}

      {children}
    </>
  )
}

interface AddressFormProps {
  record?: BillingAddress | null
  hint?: string
  errors?: Partial<Record<keyof BillingAddress, string>> | null
  heading: string
  displayDelivery?: boolean
  itemCount?: number
  children?: React.ReactNode
  formFor: 'general' | 'stripe-verification'
  /**
   * Is used in a useEffect dependency array, be aware that passing a non-memoised function
   * to this prop may cause infinite re-renders
   *
   * i.e. `onAddressChange={(address) => { doSomething() }}`
   * on every render the function is a difference reference
   *
   * Memoise with `useCallback` or `useMemo`
   */
  onAddressChange?: (
    address: Pick<
      BillingAddress,
      'line_1' | 'line_2' | 'country' | 'state' | 'city' | 'postcode'
    >
  ) => void
  onCountryCodeChange: (code: string) => void
  defaultCountryCode?: string
  isSellerBillingAddress: boolean
  allowSellerBillingAddressCountryChange: boolean
}

export default withTranslations(AddressForm)
